import {
  mutationOptions,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { Application } from '@/api/types'
import { hashId } from '@/shared/lib/hash-id'
import { orderApplications, removeApplication, updateApplication } from './cache'

/**
 * One workspace's applications -- databases and dashboards.
 *
 * Fetched per workspace rather than all at once. `GET /api/applications/`
 * returns every application the user can see across every workspace, which is
 * one response that grows with the account and invalidates as a unit.
 *
 * Kept current by the realtime layer, as `qk.workspaces()` is.
 */
export function applicationsQuery(workspaceId: number) {
  return queryOptions({
    queryKey: qk.applications(workspaceId),
    staleTime: Infinity,
    queryFn: (): Promise<Application[]> =>
      unwrap(
        getApi().GET('/api/applications/workspace/{workspace_id}/', {
          params: { path: { workspace_id: workspaceId } },
        }),
      ),
  })
}

export function useApplications(workspaceId: number) {
  return useQuery(applicationsQuery(workspaceId))
}

function optimistically(
  queryClient: QueryClient,
  workspaceId: number,
  update: (applications: Application[]) => Application[],
): Application[] | undefined {
  const key = qk.applications(workspaceId)
  const previous = queryClient.getQueryData<Application[]>(key)
  if (previous !== undefined)
    queryClient.setQueryData<Application[]>(key, update(previous))
  return previous
}

function rollback(
  queryClient: QueryClient,
  workspaceId: number,
  previous: Application[] | undefined,
): void {
  if (previous !== undefined) {
    queryClient.setQueryData(qk.applications(workspaceId), previous)
  }
}

export interface CreateApplicationValues {
  workspaceId: number
  name: string
  /**
   * The two application types this fork ships, which since M8 is also all the
   * schema offers -- the builder's and the automation's went with their Django
   * apps. Kept written out rather than taken from the schema, so adding a third
   * is a decision made here rather than one inherited.
   */
  type: 'database' | 'dashboard'
  /** Whether a new database arrives with a starter table. */
  initWithData?: boolean
}

export function createApplicationMutation(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: ({ workspaceId, name, type, initWithData }: CreateApplicationValues) =>
      unwrap(
        getApi().POST('/api/applications/workspace/{workspace_id}/', {
          params: { path: { workspace_id: workspaceId } },
          body: { name, type, init_with_data: initWithData ?? true },
        }),
      ),
    // Not optimistic: a database arrives with its own starter table, and the
    // id, order and table are all the server's to assign.
    onSuccess: (_data, { workspaceId }) =>
      queryClient.invalidateQueries({ queryKey: qk.applications(workspaceId) }),
  })
}

export function useCreateApplication() {
  return useMutation(createApplicationMutation(useQueryClient()))
}

export function updateApplicationMutation(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: ({
      applicationId,
      ...values
    }: {
      workspaceId: number
      applicationId: number
      name: string
      description?: string
    }) =>
      unwrap(
        getApi().PATCH('/api/applications/{application_id}/', {
          params: { path: { application_id: applicationId } },
          body:
            values.description === undefined
              ? { name: values.name }
              : { name: values.name, description: values.description },
        }),
      ),
    onMutate: ({ workspaceId, applicationId, ...values }) => ({
      previous: optimistically(queryClient, workspaceId, (applications) =>
        updateApplication(applications, applicationId, values),
      ),
    }),
    onError: (_error, { workspaceId }, context) =>
      rollback(queryClient, workspaceId, context?.previous),
  })
}

export function useUpdateApplication() {
  return useMutation(updateApplicationMutation(useQueryClient()))
}

export function deleteApplicationMutation(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: ({ applicationId }: { workspaceId: number; applicationId: number }) =>
      unwrap(
        getApi().DELETE('/api/applications/{application_id}/', {
          params: { path: { application_id: applicationId } },
        }),
      ),
    onMutate: ({ workspaceId, applicationId }) => ({
      previous: optimistically(queryClient, workspaceId, (applications) =>
        removeApplication(applications, applicationId),
      ),
    }),
    onError: (_error, { workspaceId }, context) =>
      rollback(queryClient, workspaceId, context?.previous),
  })
}

export function useDeleteApplication() {
  return useMutation(deleteApplicationMutation(useQueryClient()))
}

export function orderApplicationsMutation(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: ({
      workspaceId,
      applicationIds,
    }: {
      workspaceId: number
      applicationIds: number[]
    }) =>
      unwrap(
        getApi().POST('/api/applications/workspace/{workspace_id}/order/', {
          params: { path: { workspace_id: workspaceId } },
          body: { application_ids: applicationIds },
        }),
      ),
    // The realtime broadcast of this ordering arrives hashed, so the optimistic
    // write has to hash too or the two disagree about what `order` means.
    onMutate: ({ workspaceId, applicationIds }) => ({
      previous: optimistically(queryClient, workspaceId, (applications) =>
        orderApplications(applications, applicationIds.map(hashId)),
      ),
    }),
    onError: (_error, { workspaceId }, context) =>
      rollback(queryClient, workspaceId, context?.previous),
  })
}

export function useOrderApplications() {
  return useMutation(orderApplicationsMutation(useQueryClient()))
}
